import { Chip, Typography } from '@execonline-inc/execonline-ui';
import { CalendarIcon, UserIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { observer } from 'mobx-react';
import * as React from 'react';
import { T, translation, TranslationsContext } from '../../../../../../Translations';
import { shareStore } from '../../Stores/ShareStore';
import { GroupParticipant } from '../LicenseAlertsStore/Types';

interface Props {
  participants: ReadonlyArray<GroupParticipant>;
  // Addresses that matched nobody in the organization. They sit with the rest
  // of the people who cannot enrol, because that is what they are.
  unknownEmails: ReadonlyArray<string>;
  // Anyone who cannot enrol is worth removing from the invite on the spot,
  // whether they hold no license or matched nobody in the organization.
  removable: boolean;
}

// Far enough out to leave time to renew, close enough that it is news. The
// group header's own chip covers the harder case, a license that lapses before
// the collection's last course ends.
const ENDING_SOON_DAYS = 60;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const endsSoon = (endOn: Date): boolean => {
  const days = (endOn.getTime() - Date.now()) / MS_PER_DAY;

  return days >= 0 && days <= ENDING_SOON_DAYS;
};

// License dates carry no time of day. They arrive as ISO dates and parse to UTC
// midnight, so they have to be read back in UTC; formatting them in the
// viewer's zone renders the previous day everywhere west of Greenwich.
const formatDate = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

function ParticipantRows({ participants, unknownEmails, removable }: Props) {
  const ts = React.useContext(TranslationsContext);

  const removalControl = (email: string) => {
    const participant = shareStore.participants.find(
      (candidate) => candidate.kind === 'user' && candidate.email === email,
    );

    if (!removable || !participant) {
      return <></>;
    }

    return (
      <button
        type="button"
        className="flex h-4 w-4 flex-none items-center justify-center rounded text-muted-foreground hover:text-foreground"
        onClick={() => shareStore.removeParticipant(participant)}
        aria-label={translation('Remove participant')(ts)}
        data-intercom-target="share-license-alert-remove-participant"
      >
        <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    );
  };

  return (
    <div className="flex max-h-60 flex-col gap-0.5 overflow-y-auto p-3">
      {participants.map((participant) => (
        <div
          key={participant.id}
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-2 py-2 odd:bg-muted/40"
        >
          <UserIcon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <Typography variant="bodyMedium" fontWeight="medium">
              {participant.firstName} {participant.lastName}
            </Typography>
            <Typography variant="caption" className="block truncate text-muted-foreground">
              {participant.email}
            </Typography>
          </div>
          <div className="flex min-w-0 flex-none flex-col items-end gap-0.5">
            <Typography variant="caption" className="text-right text-muted-foreground">
              {participant.licenseLabel
                .map((label) => <span key="label">{label}</span>)
                .getOrElse(() => (
                  <T kind="No license" />
                ))}
            </Typography>
            {participant.licenseEndOn
              .map((licenseEndOn) => (
                <div key="dates" className="flex items-center gap-1.5 text-muted-foreground">
                  <CalendarIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
                  <Typography variant="caption" className="tabular-nums">
                    {participant.licenseStartOn
                      .map((start) => `${formatDate(start)} - ${formatDate(licenseEndOn)}`)
                      .getOrElseValue(formatDate(licenseEndOn))}
                  </Typography>
                  {endsSoon(licenseEndOn) && (
                    <Chip className="bg-primary/10 text-primary" size="sm" radius="full">
                      <T kind="Ending soon" />
                    </Chip>
                  )}
                </div>
              ))
              .getOrElseValue(<></>)}
          </div>
          {removalControl(participant.email)}
        </div>
      ))}

      {unknownEmails.map((email) => {
        return (
          <div
            key={email}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-2 py-2 odd:bg-muted/40"
          >
            <UserIcon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <Typography variant="bodyMedium" fontWeight="medium" className="truncate">
                {email}
              </Typography>
            </div>
            <Typography variant="caption" className="flex-none text-muted-foreground">
              <T kind="Unknown participant" />
            </Typography>
            {removalControl(email)}
          </div>
        );
      })}
    </div>
  );
}

export default observer(ParticipantRows);
